import { Divider, Space, Tabs, Button, Typography } from '@myua/kit';
import dayjs from 'dayjs';
import filesize from 'filesize';
import React, { useEffect, useMemo, useState, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, generatePath, useHistory, useRouteMatch } from 'react-router-dom';
import { Creative1 } from '../../../../general/icons/Creative';
import { _urls } from '../../../../general/apiScenarios';
import { formatDuration } from '../../../../general/components/FullscreenPreview/FileViewer';
import { Media } from '../../../../general/components/FullscreenPreview/Media';
import {
  FileMetadata,
  MediaType,
  getDefinition,
  ExtensionDefinition,
  getFileViewerLinkByDefinition,
} from '../../../../general/components/FullscreenPreview/model';
import { useAutomaticViewerChoice } from '../../../../general/components/FullscreenPreview/useAutomaticViewerChoice';
import {
  applyViewerChoice,
  sanitizeViewer,
} from '../../../../general/components/FullscreenPreview/viewerChoice';
import { AutomaticViewerStatus } from '../../../../general/components/FullscreenPreview/AutomaticViewerStatus';
import { ViewerSwitch } from '../../../../general/components/FullscreenPreview/ViewerSwitch';
import { hubRoutes, urlDirectDownload } from '../../../../general/urls';
import {
  Asset,
  AssetDetails as AssetDetailsType,
  AssetPerm,
  getAssetLicenses,
  isAssetDownloadAllowed,
  urlArchive,
  urlArchiveShare,
  urlCompressedTreeProvider,
} from '../../../api';
import { LicenseStatusTag } from './LicenseStatusTag';
import { isPerpetualExpiration } from '../../../../general/fontLicense/model';
import type { FontLicense } from '../../../../general/model';
import { getMagicStudioEnabled, getUser } from '../../app/selectors';
import { FavoriteToggle, FavoriteToggleMultiple } from '../../favorite/components/FavoriteToggle';
// import { SpineExporter } from '../../icons/SpineExporter';
import { PackToggleMultiple } from '../../pack/components/PackToggle';
import { getSelectedIds as getSelectedProjectIds } from '../../project/selectors';
import { Page, routes } from '../../router/model';
import { getPage } from '../../router/selectors';
import {
  ShareAssetButton,
  ShareAssetButtonMultiple,
} from '../../sharing/components/ShareAssetButton';
import { getPublicDownloadFilterFn, getSharingId } from '../../sharing/selectors';
import { BindTagsButtonMultiple } from '../../tagBinder/components/BindTagsButton';
import {
  getData,
  getDetails,
  getDetailsAssets,
  getDetailsAssetsSize,
  getDetailsLoading,
  getDetailsVisible,
  getIsOriginalView,
  getMode,
  getSelected,
  getSlotHeight,
} from '../selectors';
import '../styles.scss';
import { DownloadButton } from './DownloadButton';
import { GoogleBulkButton } from '../../googleBulk/components/GoogleBulkButton';
import { Related } from './Related';
import { Comments } from '../../comment/components/Comments';
import { Tags } from './Tags';
import { AssetLinks } from '../../assetLink/components/AssetLinks';
import { creativeHubExternalURL } from '../model';
import { GoogleDrive } from '../../icons/GoogleDrive';
import { cssVarKey, NodeFlag } from '../../../../general/model';
import { FigmaButtonRaw } from '../../../../creativeHub/modules/figmaImport/FigmaButton';
import { getFigmaImportJobs } from '../../../../creativeHub/modules/figmaImport/logic';
import { SlotDragHandler } from './SlotDragHandler';
import { GoogleBulkMode } from '../../googleBulk/model';
import { PreviewButton } from '../../preview/components/PreviewButton';
import { isCustomPreviewAllowed } from '../../preview/model';
import * as finderActions from '../actions';
import { View3DButton } from '../../fullscreenPreview/components/View3DButton';
import { useSimilarButton } from '../../search/components/useSimilarButton';
import { Image } from '@myua/icons/dist/bulk16';
import { SidebarBasketButton } from '../../magicStudio/components/BasketButton';
import {
  DeleteAssetButton,
  DeleteAssetButtonMultiple,
} from '../../uploader/components/DeleteAssetButton';
import { getCanUpload } from '../../uploader/selectors';
import { DetailsSkeleton, ProgressBar } from '../../skeleton/components/Skeleton';

export const AssetDetails = () => {
  const selected = useSelector(getSelected);
  const visible = useSelector(getDetailsVisible);

  // Сайдбар полностью закрыт — ничего не рендерим (MYUA-16254).
  if (!visible) {
    return null;
  }

  if (selected.ids.length === 1) {
    return <Single />;
  }

  return <Multiple />;
};

const Multiple = () => {
  const assets = useSelector(getDetailsAssets);
  const size = useSelector(getDetailsAssetsSize);
  const { total } = useSelector(getData);
  const mode = useSelector(getMode);
  const projectIds = useSelector(getSelectedProjectIds);
  const isEmpty =
    assets.length === 0 &&
    (mode !== Page.viewer || total.read.count === 0 || projectIds.length === 0);
  const isTotal = assets.length === 0;
  const visible = useSelector(getDetailsVisible);
  const page = useSelector(getPage);
  const sharingId = useSelector(getSharingId);
  const canUpload = useSelector(getCanUpload);

  const publicDownloadFilterFn = useSelector(getPublicDownloadFilterFn);
  const downloadableAssetIds = useMemo(
    () => publicDownloadFilterFn(assets).map(({ id }) => id),
    [assets, publicDownloadFilterFn],
  );

  return (
    <div className="al-finder-details">
      {visible && !isEmpty ? (
        <div className="al-finder-details__info">
          <div className="al-finder-details__name">
            {isTotal ? (
              <>
                Total: {total.read.count} {total.read.count === 1 ? 'asset' : 'assets'}
              </>
            ) : (
              <>Selected: {assets.length} assets</>
            )}
          </div>
          <div className="al-finder-details__size">
            Size: {filesize(isTotal ? total.read.size : size)}
          </div>
          <div className="al-finder-details__multiple-buttons">
            <DownloadButton assets={assets} />
            <GoogleBulkButton
              mode={GoogleBulkMode.assets}
              assetIds={downloadableAssetIds}
              sharingId={page === Page.sharing ? sharingId : undefined}
              showLabel
            />
            <ShareAssetButtonMultiple assets={assets} />
            <PackToggleMultiple assets={assets} />
            <FavoriteToggleMultiple assets={assets} />
            <BindTagsButtonMultiple />
            {!isTotal && canUpload && <DeleteAssetButtonMultiple assets={assets} />}
          </div>
        </div>
      ) : null}
    </div>
  );
};

function useIsPreviewActive() {
  const check1 = useRouteMatch('/:whatever/' + routes.preview) !== null;
  const check2 = useRouteMatch(routes.sharing + routes.preview) !== null;

  return check1 || check2;
}

const FigmaButton: React.FC<{ details: AssetDetailsType | undefined }> = ({ details }) => {
  const figmaImportJob = useSelector(getFigmaImportJobs).find(
    (x) => x.originalNode.id === details?.id,
  );

  if (!details) return null;
  if (!details.flags.includes(NodeFlag.figma_file)) return null;
  if (!figmaImportJob) {
    return (
      <FigmaButtonRaw
        // figma button uses raw html button because it's used in crhub as well
        // once crhub is moved to antd, this can be changed to Button
        className={`${cssVarKey} ant-btn ant-btn-default ant-btn-color-default ant-btn-variant-outlined aa-kit-btn`}
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        nodes={[details]}
      >
        <span>Import</span>
      </FigmaButtonRaw>
    );
  }
  if (figmaImportJob.status === 'pending')
    return (
      <FigmaButtonRaw
        // figma button uses raw html button because it's used in crhub as well
        // once crhub is moved to antd, this can be changed to Button
        className={`${cssVarKey} ant-btn ant-btn-default ant-btn-color-default ant-btn-variant-outlined aa-kit-btn`}
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        nodes={[details]}
        disabled
      >
        <span>Import in progress</span>
      </FigmaButtonRaw>
    );
  else if ('error' in figmaImportJob.status) {
    return (
      <FigmaButtonRaw
        // figma button uses raw html button because it's used in crhub as well
        // once crhub is moved to antd, this can be changed to Button
        className={`${cssVarKey} ant-btn ant-btn-default ant-btn-color-default ant-btn-variant-outlined aa-kit-btn`}
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        nodes={[details]}
      >
        <span>Import error</span>
      </FigmaButtonRaw>
    );
  } else if ('node_id' in figmaImportJob.status) {
    return (
      <FigmaButtonRaw
        // figma button uses raw html button because it's used in crhub as well
        // once crhub is moved to antd, this can be changed to Button
        className={`${cssVarKey} ant-btn ant-btn-default ant-btn-color-default ant-btn-variant-outlined aa-kit-btn`}
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        nodes={[details]}
      >
        <span>Import complete</span>
      </FigmaButtonRaw>
    );
  }

  figmaImportJob.status satisfies never;
  return null;
};

const Single = () => {
  const dispatch = useDispatch();
  const details = useSelector(getDetails).asset;
  const loading = useSelector(getDetailsLoading);
  const sharingId = useSelector(getSharingId);
  const history = useHistory();
  const previewActive = useIsPreviewActive();
  const _details = details !== undefined && previewActive === false ? details : null;
  const user = useSelector(getUser);
  const slotHeight = useSelector(getSlotHeight);
  const asset = useSelector(getDetailsAssets)[0] as Asset | undefined;
  const isOriginalView = useSelector(getIsOriginalView);
  const similarButton = useSimilarButton(details);
  const canUpload = useSelector(getCanUpload);
  // Magic Studio is gated per instance (MYUA-16447) — see app/selectors.
  const magicStudioEnabled = useSelector(getMagicStudioEnabled);

  // Разрешено ли скачивание файла (MYUA-16456). Флаг downloading_allowed надёжно приходит
  // в списке ассетов (asset), поэтому берём его как приоритетный источник, а details — как
  // запасной. Достаточно одного false из любого источника, чтобы считать скачивание запрещённым.
  const isDownloadAllowed =
    !!details &&
    isAssetDownloadAllowed(details) &&
    (asset === undefined || isAssetDownloadAllowed(asset));

  // Выбор просмотрщика (Text / Spine) для расширений с alternativeViewers (MYUA-17117).
  // Хук вызывается до ветвлений; выбор общий с полноэкранным режимом.
  const {
    viewer,
    setViewer,
    status: viewerStatus,
    errorCode,
    retry,
  } = useAutomaticViewerChoice(
    _details ? getDefinition(_details.name) : undefined,
    _details?.download_url,
  );

  let mediaDefinition: ExtensionDefinition | undefined;
  let originalButton: ReactNode;

  if (_details) {
    const assetDefinition = getDefinition(_details.name);

    if (assetDefinition.mt === MediaType.Asset3D) {
      if (_details.preview && !isOriginalView) {
        mediaDefinition = getDefinition(`.${_details.preview.extension}`);
        originalButton = (
          <View3DButton
            onClick={() => dispatch(finderActions.originalViewEnabled())}
            className="al-finder-details-original"
          />
        );
      } else {
        mediaDefinition = assetDefinition;
      }
    } else if (assetDefinition.alternativeViewers?.length) {
      const chosen = sanitizeViewer(assetDefinition, viewer);
      mediaDefinition = applyViewerChoice(assetDefinition, chosen);
      originalButton = (
        <ViewerSwitch
          definition={assetDefinition}
          value={chosen}
          onChange={setViewer}
          className="al-finder-details-original"
        />
      );
    } else {
      if (_details.preview) {
        mediaDefinition = getDefinition(`.${_details.preview.extension}`);
      } else {
        mediaDefinition = assetDefinition;
      }
    }
  }

  const onClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (details === undefined) return;
    if (e.detail === 2) {
      if (mediaDefinition?.mt === MediaType.Compressed) return;
      // double click
      history.push(generatePath(routes.preview, { assetID: details.id }));
    }
  };

  const [activeKey, setActiveKey] = useState<string | undefined>();

  // Лицензии файла (MYUA-16456). Legal-вкладку показываем только авторизованному
  // пользователю и только когда у ассета есть лицензии. У архива со шрифтами их
  // несколько — по одной на шрифт внутри.
  const licenses = details ? getAssetLicenses(details) : [];

  const [meta, setMeta] = useState<FileMetadata | null>(null);
  useEffect(() => {
    setMeta(null);
  }, [_details?.id]);

  // Пока деталей ещё нет, слот превью и блок информации показываем скелетоном, а не
  // спиннером поверх серой плиты. Если детали уже есть (переключились на другой ассет),
  // старые остаются на экране, а о загрузке сообщает полоса сверху.
  const showSkeleton = loading && !details;

  return (
    <div className="al-finder-details">
      <div className="al-finder-details__image-slot" style={{ height: slotHeight }}>
        <div
          className={
            showSkeleton
              ? 'al-finder-details__image-slot-inner al-skeleton'
              : 'al-finder-details__image-slot-inner'
          }
          onClick={onClick}
        >
          {originalButton}

          {_details && viewerStatus !== 'ready' ? (
            <AutomaticViewerStatus status={viewerStatus} errorCode={errorCode} onRetry={retry} />
          ) : _details && mediaDefinition ? (
            <Media
              filename={_details.name}
              src={getFileViewerLinkByDefinition(
                mediaDefinition,
                _details.download_url ?? '',
                _details.preview?.url ?? '',
              )}
              type={mediaDefinition.mt}
              onMetadataAvailable={setMeta}
              invert
              compact
              autoPlay
              muted
              compressedFileTreeProvider={{
                loadTreeURL: urlCompressedTreeProvider(_details.id ?? 0, sharingId),
                fileURLGetter: (path: string) =>
                  sharingId
                    ? urlArchiveShare(_details.id ?? 0, path, sharingId)
                    : urlArchive(_details.id ?? 0, path),
                // Архив с неодобренными лицензиями шрифтов: структуру смотреть можно,
                // извлекать файлы — нет (бэкенд ответит 403).
                openEntriesAllowed: isAssetDownloadAllowed(_details),
              }}
              imageMetadataProvider={
                user
                  ? {
                      // fs.metadata will return 401 if user is not authenticated
                      loadURL: _urls.crhub.fs.metadata(_details.id),
                    }
                  : null
              }
              layerTool={undefined}
            />
          ) : null}

          <SlotDragHandler />
        </div>
      </div>
      {/* Полоса прогресса живёт снаружи прокручиваемого блока: внутри него
          position: absolute уезжало бы вместе с контентом при рефетче того же ассета. */}
      <div className="al-finder-details__main-wrap" aria-busy={loading}>
        {loading && details && <ProgressBar />}
        <div
          className="al-finder-details__main"
          key={details?.id} // задаем ключ, чтобы при смене ассета происходила прокрутка вверх
        >
          {showSkeleton && <DetailsSkeleton />}
          {details ? (
            <div className="al-finder-details__info">
              <div className="al-finder-details__name">
                {details.name}
                <FavoriteToggle asset={details} />
              </div>
              <div className="al-finder-details__buttons">
                <Space wrap>
                  <DownloadButton assets={[details]} />
                  {details.can_create_direct_download_link && isDownloadAllowed && (
                    // Прямую ссылку на скачивание (Google Drive) прячем, если бэкенд запретил
                    // скачивание: цветной логотип Google не «гаснет» в disabled-состоянии и
                    // выглядит доступным, поэтому кнопку скрываем целиком (MYUA-16456).
                    <Button
                      icon={<GoogleDrive />}
                      href={urlDirectDownload(details, sharingId) ?? undefined}
                      target="_blank"
                      title="View file on Google Drive"
                    />
                  )}
                  {/* asset (из списка) — надёжный источник downloading_allowed; details запасной */}
                  <ShareAssetButton asset={asset ?? details} />
                  <FigmaButton details={details} />
                  {details.perms.includes(AssetPerm.delete) && canUpload && (
                    <DeleteAssetButton asset={details} />
                  )}
                </Space>
                {magicStudioEnabled && <SidebarBasketButton asset={details} />}
              </div>
              <Tabs
                className="al-finder-details__tabs"
                activeKey={activeKey}
                onChange={(key) => setActiveKey(key)}
                animated={false}
                size="small"
                items={[
                  {
                    label: 'Information',
                    key: 'information',
                    children: (
                      <>
                        <Tags tags={details.tags} />

                        <div>
                          <div className="al-finder-details-watch-buttons">
                            {details.creatives.length > 0 && (
                              <Link
                                to={generatePath(routes.previewCreative, {
                                  assetID: details.id,
                                  sharingId,
                                })}
                                className="al-finder-details-watch-buttons__button"
                              >
                                <Creative1 />
                                <span>Watch Creative</span>
                              </Link>
                            )}

                            {/*{details.spine_exporter && (*/}
                            {/*  <a*/}
                            {/*    className="al-finder-details-watch-buttons__button"*/}
                            {/*    href={details.spine_exporter}*/}
                            {/*    target="_blank"*/}
                            {/*    rel="noopener noreferrer"*/}
                            {/*  >*/}
                            {/*    <SpineExporter />*/}
                            {/*    Watch Spine*/}
                            {/*  </a>*/}
                            {/*)}*/}

                            {details.animation_preview_id ? (
                              <Link
                                to={generatePath(routes.previewSingle, {
                                  assetID: details.animation_preview_id,
                                  sharingId,
                                })}
                                className="al-finder-details-watch-buttons__button"
                              >
                                Watch preview
                              </Link>
                            ) : null}

                            {details.animation_archive_id ? (
                              <Link
                                to={generatePath(routes.previewSingle, {
                                  assetID: details.animation_archive_id,
                                  sharingId,
                                })}
                                className="al-finder-details-watch-buttons__button"
                              >
                                Watch archive
                              </Link>
                            ) : null}

                            {similarButton.isVisible ? (
                              <div
                                className="al-finder-details-watch-buttons__button"
                                onClick={similarButton.onClick}
                              >
                                <Image className="al-finder-details-watch-buttons__similar-icon" />
                                Search similar
                              </div>
                            ) : null}
                          </div>

                          <Property label="Type">{details.type.extension}</Property>

                          <Property label="Size">{filesize(details.size)}</Property>

                          {isCustomPreviewAllowed(details) ? (
                            <Property label="Preview">
                              <PreviewButton asset={details} />
                            </Property>
                          ) : null}

                          {meta && (
                            <>
                              <Property label="Dimensions">
                                {meta.width ?? 0} × {meta.height ?? 0}
                              </Property>

                              {meta.duration && (
                                <Property label="Duration">
                                  {formatDuration(meta.duration)}
                                </Property>
                              )}
                            </>
                          )}
                          <Property label="Created date">
                            {dayjs(details.created_at).format('YYYY-MM-DD')}
                          </Property>
                          <Property label="Updated date">
                            {dayjs(details.updated_at).format('YYYY-MM-DD')}
                          </Property>
                          <Property label="Author">
                            {details.owner_first_name} {details.owner_last_name}
                          </Property>
                          <Property label="Project">
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href={generatePath(hubRoutes.folderExternal, {
                                folderID: details.project.id,
                              })}
                            >
                              {details.project.name}
                            </a>
                          </Property>

                          {details.category ? (
                            <Property label="Category">
                              <a
                                target="_blank"
                                rel="noreferrer"
                                href={generatePath(hubRoutes.folderExternal, {
                                  folderID: details.category.id,
                                })}
                              >
                                {details.category.name}
                              </a>
                            </Property>
                          ) : null}

                          {user?.is_superuser && typeof asset?.ai_search_score === 'number' ? (
                            <Property label="AI search score">{asset.ai_search_score}</Property>
                          ) : null}

                          <div>
                            <a
                              target="_blank"
                              rel="noreferrer"
                              href={creativeHubExternalURL(details)}
                            >
                              Open file in Creative Hub
                            </a>
                          </div>

                          <AssetLinks />
                        </div>
                      </>
                    ),
                  },
                  {
                    label: 'Related assets',
                    key: 'relatedAssets',
                    children: <Related />,
                  },
                  {
                    label: 'Comments',
                    key: 'comments',
                    children: <Comments outerSpinner={loading} />,
                  },
                  // Legal-вкладка (MYUA-16456): только для авторизованного пользователя
                  // и только если у ассета есть лицензии. Для гостя (user === null) скрыта.
                  ...(user && licenses.length > 0
                    ? [
                        {
                          label: 'Legal',
                          key: 'legal',
                          children: (
                            <div>
                              {licenses.map((license, index) => (
                                <LicenseBlock
                                  key={license.id}
                                  license={license}
                                  // Заголовок с именем шрифта нужен только когда лицензий
                                  // несколько (архив): иначе он дублировал бы имя ассета.
                                  fontName={licenses.length > 1 ? license.filename : null}
                                  showDivider={index > 0}
                                />
                              ))}
                            </div>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          ) : (
            <div className="al-finder-details__empty" />
          )}
        </div>
      </div>
    </div>
  );
};

// Одна лицензия на вкладке Legal. У архива со шрифтами таких блоков несколько — по
// одному на шрифт внутри; они разделены линией и подписаны именем шрифта, т.к. статус
// и срок у них могут отличаться (юрист согласует каждую лицензию отдельно).
const LicenseBlock: React.FC<{
  license: FontLicense;
  fontName: string | null | undefined;
  showDivider: boolean;
}> = ({ license, fontName, showDivider }) => (
  <div>
    {showDivider ? <Divider style={{ margin: '12px 0' }} /> : null}
    {fontName ? (
      <div className="al-finer-details-property">
        <Typography.Text strong>{fontName}</Typography.Text>
      </div>
    ) : null}
    <Property label="Status">
      <LicenseStatusTag status={license.status} />
    </Property>
    {license.license_type ? (
      <Property label="License type">{license.license_type.name}</Property>
    ) : null}
    <Property label="Projects">
      {/* null и [] одинаково означают отсутствие ограничения по проектам */}
      {license.projects === null || license.projects.length === 0
        ? 'All projects'
        : license.projects.map((project) => project.name).join(', ')}
    </Property>
    <Property label="Expiration date">
      {license.expiration_date === null || isPerpetualExpiration(license.expiration_date)
        ? 'Unlimited'
        : dayjs(license.expiration_date).format('YYYY-MM-DD')}
    </Property>
  </div>
);

const Property: React.FC<{ label: string }> = ({ label, children }) => {
  return (
    <div className="al-finer-details-property">
      <div className="al-finder-details-property__label">{label}</div>
      <div className="al-finder-details-property__value">{children}</div>
    </div>
  );
};
